/**
 * processApi.js (E9, §8.8) — el wrapper de las DOS rutas nuevas de la historia 152, hermano de
 * `operationLibraryApi.js` (que sigue siendo el wrapper de N1-N16 de #130). Una función por fila del
 * contrato §8.2; aquí no vive ninguna regla de negocio: este fichero es el único sitio de Jupiter
 * que deletrea `workflow/{id}/processTree` y `workflow/{id}/node/{nodeId}/operationCustomization`.
 *
 * Las rutas y los nombres de campo son CONTRATO (R-HANDOFF-1 / R-HANDOFF-2): se validan contra
 * `ProcessTreeRest.java` y `WorkflowOperationLinkRest.java` de la rama 3806 de Sun.
 *
 * Decisión de fichero (§8.8 E9, anotada en `plan-jupiter.md`): P2 vive AQUÍ, junto a P1, y no en
 * `operationLibraryApi.js`. Las dos rutas nuevas de esta historia entran por la misma puerta.
 */
import { apiGet } from '../../generic/Api_Functions'
import { readOperationLibraryError } from './operationLibraryApi'

/**
 * P2 — `GET workflow/{workflowId}/node/{nodeId}/operationCustomization` → `OperationCustomizationDto`
 *
 * El estado del nodo más la comparación de su job contra el de su versión de origen (`RN-01.22`,
 * S38). Solo lectura: no cambia el nodo, ni la versión, ni el editor.
 *
 * @typedef {Object} OperationCustomizationDto
 * @property {string} nodeId
 * @property {number} workflowId
 * @property {'SYNCHRONIZED'|'CUSTOMIZED'|'UNLINKED'} state
 * @property {string} code Código de la operación de origen
 * @property {number} versionNumber Número de la versión de origen
 * @property {Object} comparison El `OperationComparison` de #130 VERBATIM: los mismos bloques
 *   (`GENERAL`, `PREPARATION`, `TASKS`, `FEATURES`, `TOOLS`, `DOCUMENTS`) en el mismo orden
 *
 * Errores de contrato: `409 E-LNK-02` sobre un nodo embebido o `UNLINKED`, `404 E-LNK-01` si la
 * versión de origen ya no existe, `403 E-PRM-01` sin `OPERATION_LIBRARY`.
 *
 * @param {number} workflowId
 * @param {string} nodeId
 * @returns {Promise<OperationCustomizationDto>}
 */
export const getOperationCustomization = (workflowId, nodeId) =>
  apiGet('workflow/' + workflowId + '/node/' + nodeId + '/operationCustomization')

/**
 * P1 — `GET workflow/{workflowId}/processTree` → `ProcessTreeDto` (§8.2, §8.4)
 *
 * El árbol LIGERO de una plantilla para la sección Template de la paleta (CA1.2, S4): filas JOB y
 * WORKFLOW anidadas a todos los niveles, con el `operationLink` resumido de cada JOB. **No lleva
 * jobs, ni tareas, ni recursos, ni posiciones** (R6): pintar el árbol nunca carga el workflow.
 *
 * Solo lectura: desplegar un proceso no crea, no modifica y no bloquea nada (POL6, `RN-01.3`). El
 * permiso es el del editor (`WORKFLOWS`), no el de la biblioteca: sin `OPERATION_LIBRARY` el árbol
 * se despliega igual (`RN-G16`, S3).
 *
 * @typedef {Object} ProcessTreeNodeDto
 * @property {string} nodeId
 * @property {string} code
 * @property {string|null} name
 * @property {'JOB'|'WORKFLOW'} nodeType
 * @property {number} workflowId El workflow que CONTIENE el nodo (lo que necesita una copia)
 * @property {number|null} subWorkflowId Solo en un nodo WORKFLOW
 * @property {string|null} operationCode Solo en un JOB vinculado — la clave del censo (D11-bis)
 * @property {number|null} operationVersionNumber
 * @property {'SYNCHRONIZED'|'CUSTOMIZED'|'UNLINKED'|null} linkState
 * @property {boolean} countsInCensus `true` ⇔ JOB con vínculo vivo
 * @property {ProcessTreeNodeDto[]} children Solo en un nodo WORKFLOW
 *
 * @typedef {Object} ProcessTreeDto
 * @property {number} workflowId
 * @property {string} code
 * @property {string|null} name
 * @property {string|null} description
 * @property {ProcessTreeNodeDto[]} root Nodos de primer nivel
 * @property {string[]} unreadableBranchNodeIds Ramas DECLARADAS, no ocultadas (`RN-01.3b`, S6)
 *
 * Errores de contrato: `404 E-CMP-01` (`Process not found`) si la plantilla ya no existe (S7).
 *
 * @param {number} workflowId
 * @returns {Promise<ProcessTreeDto>}
 */
export const getProcessTree = (workflowId) => apiGet('workflow/' + workflowId + '/processTree')

/**
 * Normaliza el cuerpo de error de contrato de las dos rutas nuevas a `{ status, error, context }`,
 * donde `error` es el literal de negocio VERBATIM —que es la propia clave i18n (§8.9)—.
 *
 * Es el MISMO lector que el de la biblioteca de operaciones: los cuerpos de error de esta historia
 * son los de #130 §8.8.6 ampliados con `E-CMP-*` y `E-ORG-01`, con la misma forma. Se reexporta con
 * nombre propio para que un consumidor de P1/P2 no tenga que importar de dos ficheros.
 */
export const readProcessError = readOperationLibraryError

/**
 * Los CUATRO literales de contrato que esta historia añade al guardado del workflow (§8.9, J41-J44)
 * y que el usuario tiene que poder leer en su idioma. El literal ES la clave i18n: Jupiter pinta
 * `t_(body.error)` y no vuelve a traducir un código de error.
 *
 * - `E-CMP-01` `Process not found` — la plantilla ya no existe (S7, CU-02 A3)
 * - `E-CMP-02` `Only JOB nodes can be copied from a process` — copia de algo que no es un JOB
 * - `E-CMP-03` `Unknown operation link intent` — intención desconocida en `Node.operationLink` (H1)
 * - `E-ORG-01` `Origin data is not valid for this node` — origen en una fila de biblioteca (`IN9`)
 */
export const PROCESS_CONTRACT_ERRORS = [
  'Process not found',
  'Only JOB nodes can be copied from a process',
  'Unknown operation link intent',
  'Origin data is not valid for this node'
]

/**
 * El literal de contrato de esta historia que trae un rechazo del guardado, o `''` si lo que falló
 * no es uno de ellos. Se lee del MISMO cuerpo de error que el resto (`readProcessError`) y por el
 * MISMO camino que ya mostraba el fallo del guardado: ni ruta nueva, ni diálogo nuevo.
 *
 * @param {*} thrown
 * @returns {string} El literal traducible, o `''`
 */
export const readProcessSaveError = (thrown) => {
  const { error } = readProcessError(thrown)
  return PROCESS_CONTRACT_ERRORS.includes(error) ? error : ''
}
